import React, { useState } from "react";
import { Button } from "../components/Button.jsx";
import { useGame } from "@empirica/core/player/classic/react";
import { getTaskConfig } from "../utils/taskConfig.js";

export function NoAIControlInstructions({ next }) {
  const game = useGame();
  const taskConfig = getTaskConfig(game?.get("treatment") || {});
  const [answers, setAnswers] = useState({ q1: "", q2: "", q3: "", q4: "" });
  const [showError, setShowError] = useState(false);

  const correctAnswers = {
    q1: "group",
    q2: "convincing",
    q3: "individual",
    q4: "edit"
  };

  const handleAnswerChange = (question, value) => {
    setAnswers({ ...answers, [question]: value });
    setShowError(false);
  };

  const handleNext = () => {
    const allCorrect = Object.keys(correctAnswers).every(
      key => answers[key] === correctAnswers[key]
    );
    
    if (allCorrect) {
      next();
    } else {
      setShowError(true);
    }
  };

  return (
    <div className="mt-3 sm:mt-5 p-20">
      <h3 className="text-lg leading-6 font-medium text-gray-900">
        Instructions
      </h3>
      <div className="mt-3 mb-6 space-y-6 text-gray-800">
        {/* 1. Objective of the game */}
        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">1. Objective of the Game</h4>
          <p>
            You will be playing this game with other participants. The objective is to make an accurate prediction, which will be determined by taking the <strong>majority vote</strong> of your group.
          </p>
          {taskConfig.introPrompt && (
            <p className="mt-2">{taskConfig.introPrompt}</p>
          )}
        </div>

        {/* 2. Process of the game */}
        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">2. Process of the Game</h4>
          <div className="space-y-2">
            {taskConfig.quizEnabled && (
              <p>
                <strong>Step 0 (Quiz):</strong> You will first complete a <strong>10-question quiz</strong> based on loan cases. This quiz is used to evaluate baseline knowledge in the domain. You will not see the correct answers during the quiz.
              </p>
            )}
            <p>
              <strong>Step 1:</strong> You will make your <strong>own individual prediction</strong>.
            </p>
            <p>
              <strong>Step 2:</strong> You will enter a <strong>6-minute group discussion</strong> with other participants. During this discussion, you should deliberate and try to convince others of your perspective.
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li><strong>Turn-taking:</strong> Players take turns speaking in a rotating order.</li>
              <li><strong>Time limit:</strong> Each player has <strong>45 seconds</strong> to speak when it's their turn.</li>
              <li><strong>Automatic turn advance:</strong> If you don't send a message within 45 seconds, your turn will automatically pass to the next player.</li>
              <li><strong>You can always edit:</strong> You can type and edit your message at any time, even when it's not your turn. However, you can only <strong>send</strong> your message when it's your turn.</li>
            </ul>
            <p>
              <strong>Step 3:</strong> Finally, you will submit your <strong>final individual prediction</strong>.
            </p>
          </div>
        </div>

        {/* 3. Reward setup */}
        <div className="bg-blue-50 rounded-md shadow-sm border border-blue-200 p-4">
          <h4 className="font-semibold text-blue-900 mb-3 text-lg">3. Reward Setup</h4>
          <div className="space-y-2 text-blue-800">
            <p>
              <strong>Base Payment:</strong> You will receive $3 (roughly $9/hour).
            </p>
            {taskConfig.quizEnabled && (
              <p>
                <strong>Quiz Bonus:</strong> Quiz correctness is linked to bonus. Each correct quiz question is worth <strong>$0.3</strong>.
              </p>
            )}
            <p>
              <strong>Accuracy Bonus:</strong> In addition to the base payment, you may receive <strong>$3.5</strong> if the <strong>majority vote of your group</strong> (not your individual final prediction) is accurate.
            </p>
            <p>
              <strong>Most Convincing Reasoner Bonus:</strong> The one player who receives the highest number of votes for providing the most convincing reasons will earn an additional <strong>$1.5</strong> bonus.
            </p>
          </div>
        </div>

        {/* Comprehension Questions */}
        <div className="bg-yellow-50 rounded-md shadow-sm border border-yellow-200 p-4">
          <h4 className="font-semibold text-yellow-900 mb-3 text-lg">Please answer the following questions to proceed:</h4>
          
          <div className="space-y-4">
            {/* Question 1 */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                1. The $3.5 accuracy bonus is based on which outcome?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q1"
                    value="individual"
                    checked={answers.q1 === "individual"}
                    onChange={(e) => handleAnswerChange("q1", e.target.value)}
                    className="mr-2"
                  />
                  <span>Your individual final prediction</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q1"
                    value="group"
                    checked={answers.q1 === "group"}
                    onChange={(e) => handleAnswerChange("q1", e.target.value)}
                    className="mr-2"
                  />
                  <span>The majority vote of your group</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q1"
                    value="initial"
                    checked={answers.q1 === "initial"}
                    onChange={(e) => handleAnswerChange("q1", e.target.value)}
                    className="mr-2"
                  />
                  <span>Your initial prediction</span>
                </label>
              </div>
            </div>

            {/* Question 2 */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                2. How can you earn the additional $1.5 bonus for being the most convincing reasoner?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q2"
                    value="accurate"
                    checked={answers.q2 === "accurate"}
                    onChange={(e) => handleAnswerChange("q2", e.target.value)}
                    className="mr-2"
                  />
                  <span>By making the most accurate prediction</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q2"
                    value="convincing"
                    checked={answers.q2 === "convincing"}
                    onChange={(e) => handleAnswerChange("q2", e.target.value)}
                    className="mr-2"
                  />
                  <span>By receiving the most votes for providing the most convincing reasons</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q2"
                    value="participating"
                    checked={answers.q2 === "participating"}
                    onChange={(e) => handleAnswerChange("q2", e.target.value)}
                    className="mr-2"
                  />
                  <span>By participating in the discussion</span>
                </label>
              </div>
            </div>

            {/* Question 3 */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                3. What should you do during the group discussion?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q3"
                    value="listen"
                    checked={answers.q3 === "listen"}
                    onChange={(e) => handleAnswerChange("q3", e.target.value)}
                    className="mr-2"
                  />
                  <span>Only listen to others</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q3"
                    value="individual"
                    checked={answers.q3 === "individual"}
                    onChange={(e) => handleAnswerChange("q3", e.target.value)}
                    className="mr-2"
                  />
                  <span>Deliberate and try to convince others of your perspective</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q3"
                    value="agree"
                    checked={answers.q3 === "agree"}
                    onChange={(e) => handleAnswerChange("q3", e.target.value)}
                    className="mr-2"
                  />
                  <span>Agree with everyone</span>
                </label>
              </div>
            </div>

            {/* Question 4 - Turn-taking */}
            <div>
              <p className="font-medium text-gray-900 mb-2">
                4. During the group discussion, when can you edit your message text?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q4"
                    value="only-turn"
                    checked={answers.q4 === "only-turn"}
                    onChange={(e) => handleAnswerChange("q4", e.target.value)}
                    className="mr-2"
                  />
                  <span>Only when it's your turn</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q4"
                    value="edit"
                    checked={answers.q4 === "edit"}
                    onChange={(e) => handleAnswerChange("q4", e.target.value)}
                    className="mr-2"
                  />
                  <span>At any time, but you can only send when it's your turn</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="q4"
                    value="never"
                    checked={answers.q4 === "never"}
                    onChange={(e) => handleAnswerChange("q4", e.target.value)}
                    className="mr-2"
                  />
                  <span>You cannot edit your message</span>
                </label>
              </div>
            </div>
          </div>

          {showError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 text-sm">
                Please answer all questions correctly before proceeding.
              </p>
            </div>
          )}
        </div>
      </div>
      <Button handleClick={handleNext} autoFocus>
        <p>Next</p>
      </Button>
    </div>
  );
}
